import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MuiPopover from '@material-ui/core/Popover';
import MuiPaper from '@material-ui/core/Paper';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiButton from '@material-ui/core/Button';
import MuiBox from '@material-ui/core/Box';
import MuiDivider from '@material-ui/core/Divider';
import MuiTooltip from '@material-ui/core/Tooltip';
import MuiTextField from '@material-ui/core/TextField';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiSwitch from '@material-ui/core/Switch';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';
import MuiTypography from '@material-ui/core/Typography';
import FilterListIcon from '@material-ui/icons/FilterList';
import ClearIcon from '@material-ui/icons/Clear';

// Função para converter formato americano (mm/dd/yyyy) para ISO
const getYYYYMMDDfromISO = isoDate => {
  if (!isoDate || typeof isoDate !== 'string') return '';
  // Returns only the date part (YYYY-MM-DD) from an ISO string
  return isoDate.split('T')[0];
};

const styles = theme => ({
  filterButton: {
    padding: 2,
    marginLeft: 4,
    verticalAlign: 'top',
  },
  filterActive: {
    color: theme.palette.primary.main,
  },
  popover: {
    minWidth: 250,
    maxWidth: 350,
  },
  filterContent: {
    padding: theme.spacing(2),
  },
  filterActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 2),
  },
  filterActionButton: {
    marginLeft: theme.spacing(1),
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: '100%',
  },
  rangeInputs: {
    display: 'flex',
    alignItems: 'center',
    '& > *:first-child': {
      marginRight: theme.spacing(1),
    },
  },
  dateInputs: {
    display: 'flex',
    flexDirection: 'column',
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
  },
  filterTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
});

/**
 * Component for advanced column filtering in tables
 */
const ColumnFilter = props => {
  const {
    classes,
    columnId,
    columnLabel,
    filterType,
    hasFilter,
    onOpenFilter,
    onApplyFilter,
    onClearFilter,
    filterValues,
    onFilterValueChange,
    options,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenFilter = event => {
    setAnchorEl(event.currentTarget);
    if (onOpenFilter) {
      onOpenFilter(columnId, event.currentTarget);
    }
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = () => {
    if (onApplyFilter) {
      onApplyFilter();
    }
    handleCloseFilter();
  };

  const handleClearFilter = () => {
    if (onClearFilter) {
      onClearFilter();
    }
    handleCloseFilter();
  };

  const handleFilterValueChange = (key, value) => {
    if (onFilterValueChange) {
      onFilterValueChange(key, value);
    }
  };

  // Renders filter content based on type
  const renderFilterContent = () => {
    switch (filterType) {
      case 'date':
        return (
          <>
            <MuiTypography variant="subtitle2" className={classes.filterTitle}>
              Filter by date range
            </MuiTypography>
            <MuiFormControl className={classes.formControl}>
              <MuiTextField
                label="From"
                type="date"
                value={getYYYYMMDDfromISO(filterValues?.startDate)}
                onChange={e =>
                  handleFilterValueChange('startDate', e.target.value ? `${e.target.value}T00:00:00.000Z` : null)
                }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                variant="outlined"
                size="small"
              />
            </MuiFormControl>
            <MuiFormControl className={classes.formControl}>
              <MuiTextField
                label="To"
                type="date"
                value={getYYYYMMDDfromISO(filterValues?.endDate)}
                onChange={e =>
                  handleFilterValueChange('endDate', e.target.value ? `${e.target.value}T23:59:59.999Z` : null)
                }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                variant="outlined"
                size="small"
              />
            </MuiFormControl>
          </>
        );

      case 'text':
        return (
          <>
            <MuiTypography variant="subtitle2" className={classes.filterTitle}>
              Filter by text
            </MuiTypography>
            <MuiFormControl className={classes.formControl}>
              <MuiTextField
                value={filterValues?.value || ''}
                onChange={e => handleFilterValueChange('value', e.target.value)}
                placeholder="Type text or paste multiple IDs separated by comma, space, or line break..."
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={3}
                helperText={(() => {
                  const currentValue = filterValues.value || '';
                  if (!currentValue.trim()) {
                    return 'Single value: searches for match. Multiple values: finds any of the values (e.g: 123, 456, 789)';
                  }

                  // Count values
                  const hasMultipleValues =
                    /[\s,\n\r]+/.test(currentValue) &&
                    currentValue.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;
                  // Add warning if more than 1000 values and less than 5000
                  if (hasMultipleValues) {
                    const valueCount = currentValue.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length;
                    if (valueCount > 5000) {
                      return `❌ ${valueCount} values detected. Maximum 5000 allowed. Please split into multiple queries.`;
                    } else if (valueCount > 4000) {
                      return `⚠️ ${valueCount} values detected. Approaching limit (5000 max). Consider splitting large queries.`;
                    } else if (valueCount > 1000) {
                      return `📊 ${valueCount} values detected. Large queries may take longer.`;
                    } else {
                      return `📊 ${valueCount} values detected. Will search for any of these values.`;
                    }
                  }

                  return 'Single value: searches for match. Multiple values: finds any of the values.';
                })()}
              />
            </MuiFormControl>
          </>
        );

      case 'number':
        return (
          <>
            <MuiTypography variant="subtitle2" className={classes.filterTitle}>
              Filter by number
            </MuiTypography>
            <MuiFormControl className={classes.formControl}>
              <MuiSelect
                value={filterValues?.operator || 'equals'}
                onChange={e => handleFilterValueChange('operator', e.target.value)}
                fullWidth
                variant="outlined"
                size="small">
                <MuiMenuItem value="equals">Equals</MuiMenuItem>
                <MuiMenuItem value="gt">Greater than</MuiMenuItem>
                <MuiMenuItem value="gte">Greater than or equal to</MuiMenuItem>
                <MuiMenuItem value="lt">Less than</MuiMenuItem>
                <MuiMenuItem value="lte">Less than or equal to</MuiMenuItem>
                <MuiMenuItem value="between">Between</MuiMenuItem>
              </MuiSelect>
            </MuiFormControl>

            {filterValues?.operator === 'between' ? (
              <MuiBox className={classes.rangeInputs}>
                <MuiTextField
                  value={filterValues?.min || ''}
                  onChange={e => handleFilterValueChange('min', e.target.value)}
                  placeholder="Minimum"
                  type="number"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <MuiTextField
                  value={filterValues?.max || ''}
                  onChange={e => handleFilterValueChange('max', e.target.value)}
                  placeholder="Maximum"
                  type="number"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </MuiBox>
            ) : (
              <MuiFormControl className={classes.formControl}>
                <MuiTextField
                  value={filterValues?.value || ''}
                  onChange={e => handleFilterValueChange('value', e.target.value)}
                  placeholder="Enter value..."
                  type="number"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </MuiFormControl>
            )}
          </>
        );

      case 'boolean':
        return (
          <>
            <MuiTypography variant="subtitle2" className={classes.filterTitle}>
              Filter by state
            </MuiTypography>
            <MuiFormControl className={classes.formControl}>
              <MuiFormControlLabel
                control={
                  <MuiSwitch
                    checked={filterValues?.value === true}
                    onChange={e => handleFilterValueChange('value', e.target.checked)}
                    color="primary"
                  />
                }
                label={filterValues?.value === true ? 'Yes' : 'No'}
              />
            </MuiFormControl>
          </>
        );

      case 'select':
        return (
          <>
            <MuiTypography variant="subtitle2" className={classes.filterTitle}>
              Select an option
            </MuiTypography>
            <MuiFormControl className={classes.formControl}>
              <MuiSelect
                value={filterValues?.value || ''}
                onChange={e => handleFilterValueChange('value', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                displayEmpty>
                <MuiMenuItem value="">
                  <em>All</em>
                </MuiMenuItem>
                {options?.map(option => (
                  <MuiMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MuiMenuItem>
                ))}
              </MuiSelect>
            </MuiFormControl>
          </>
        );

      default:
        return <div>Filter type not supported</div>;
    }
  };

  return (
    <>
      <MuiTooltip title={`Filter ${columnLabel}`}>
        <MuiIconButton
          className={`${classes.filterButton} ${hasFilter ? classes.filterActive : ''}`}
          onClick={handleOpenFilter}
          size="small"
          aria-label={`Filter ${columnLabel}`}>
          <FilterListIcon fontSize="small" />
        </MuiIconButton>
      </MuiTooltip>

      <MuiPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          className: classes.popover,
        }}>
        <MuiPaper>
          <MuiBox className={classes.filterContent}>{renderFilterContent()}</MuiBox>
          <MuiDivider />
          <MuiBox className={classes.filterActions}>
            <MuiButton size="small" onClick={handleClearFilter} startIcon={<ClearIcon />}>
              Clear
            </MuiButton>
            <MuiButton
              size="small"
              color="primary"
              variant="contained"
              onClick={handleApplyFilter}
              className={classes.filterActionButton}>
              Apply
            </MuiButton>
          </MuiBox>
        </MuiPaper>
      </MuiPopover>
    </>
  );
};

ColumnFilter.propTypes = {
  classes: PropTypes.object.isRequired,
  columnId: PropTypes.string.isRequired,
  columnLabel: PropTypes.string.isRequired,
  filterType: PropTypes.oneOf(['text', 'number', 'date', 'boolean', 'select']).isRequired,
  hasFilter: PropTypes.bool,
  onOpenFilter: PropTypes.func,
  onApplyFilter: PropTypes.func,
  onClearFilter: PropTypes.func,
  filterValues: PropTypes.object,
  onFilterValueChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

ColumnFilter.defaultProps = {
  hasFilter: false,
  filterValues: {},
  options: [],
};

export default withStyles(styles)(ColumnFilter);
